import { FinchConnectionDetailFragment } from '@alice-financial/alice-sdk'
import { Alert, Box, Button, Typography } from '@alice-financial/pretext-ui'
import WarningIcon from '@mui/icons-material/Warning'
import * as React from 'react'

type NeedingAuthFinchConnectionsProps = {
  needingAuthFinchConnections: FinchConnectionDetailFragment[]
  inProgressFinchConnectionId: number | null
  onResumeConnection: (connectionId: number) => void
}

/**
 * Component that displays Finch connections that have been created but still need authentication.
 * Shows warning alerts for each connection with an "Authenticate" button to resume the connection process.
 * Used in both the initial setup flow and when managing existing connections.
 */
export const NeedingAuthFinchConnections = ({
  needingAuthFinchConnections,
  inProgressFinchConnectionId,
  onResumeConnection,
}: NeedingAuthFinchConnectionsProps) => {
  if (needingAuthFinchConnections.length === 0) {
    return null
  }

  return (
    <Box sx={{ my: 2 }}>
      <Typography variant="body2" gutterBottom>
        <strong>Connections requiring authentication:</strong>
      </Typography>
      {needingAuthFinchConnections.map((conn) => (
        <Alert
          key={conn.id}
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 2 }}
          action={
            <Button
              variant="contained"
              color="primary"
              onClick={() => onResumeConnection(conn.id)}
              disabled={inProgressFinchConnectionId === conn.id}
              size="small"
            >
              {inProgressFinchConnectionId === conn.id ? 'Authenticating...' : 'Authenticate'}
            </Button>
          }
        >
          <Typography variant="subtitle2">{conn.displayLabel}</Typography>
          <Typography variant="body2">
            {conn.hasAuthenticatedEver ? 'Re-authentication required' : 'Authentication required'}
          </Typography>
        </Alert>
      ))}
    </Box>
  )
}
